import { Link, Text, View } from '@react-pdf/renderer';
import React from 'react';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

import { styles } from "@/utils/export/utils/renderNodeToPDF/renderNodeToPDF.styles";

interface MarkdownNode {
    type: string;
    value?: string;
    url?: string;
    lang?: string;
    depth?: number;
    children?: MarkdownNode[];
}

interface TableData {
    [key: string]: string | number | boolean | null | undefined;
}

const extractText = (node: MarkdownNode): { text: string; url?: string } => {
    if (node.type === 'text' && node.value) return { text: node.value };
    if (node.type === 'link' && node.children) {
        const linkText = node.children.map(child => extractText(child).text).join('');
        return { text: linkText, url: node.url };
    }
    if (node.children && node.children.length > 0) {
        const contents = node.children.map(extractText);
        return {
            text: contents.map(c => c.text).join(''),
            url: contents.find(c => c.url)?.url,
        };
    }
    return { text: '' };
};

const extractNodeText = (node: MarkdownNode): string => {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (node.value) return node.value;
    if (node.children && node.children.length > 0) {
        return node.children.map(extractNodeText).join('');
    }
    return '';
};

const renderInlineNode = (node: MarkdownNode, key: number): React.ReactNode => {
    switch (node.type) {
        case 'text':
            return <Text key={key}>{node.value}</Text>;
        case 'strong':
            return (
                <Text key={key} style={{ fontFamily: 'Bold' }}>
                    {extractText(node).text}
                </Text>
            );
        case 'emphasis':
            return (
                <Text key={key} style={{ fontStyle: 'italic' }}>
                    {extractText(node).text}
                </Text>
            );
        case 'link':
            return (
                // @ts-expect-error — @react-pdf/renderer Link component types don't accept src prop correctly
                <Link key={key} src={extractText(node).url} style={styles.link}>
                    {extractText(node).text}
                </Link>
            );
        default:
            if (node.children)
                return node.children.map((child, idx) => renderInlineNode(child, idx));
            return null;
    }
};

const renderNodeToPDF = (node: MarkdownNode, level = 0, onlyCode = false): React.ReactNode => {
    switch (node.type) {
        case 'heading':
            if (onlyCode) return null;
            return (
                <Text
                    style={
                        node.depth === 1
                            ? styles.heading1
                            : node.depth === 2
                                ? styles.heading2
                                : styles.heading3
                    }
                    key={Math.random()}
                >
                    {extractText(node).text}
                </Text>
            );

        case 'paragraph':
            if (onlyCode || !node.children) return null;
            const paragraphText = node.children.map(extractNodeText).join('').trim();
            const paragraphStyle = {
                ...styles.paragraph,
                marginBottom: paragraphText.endsWith(':') ? 15 : 8,
            };
            return (
                <Text style={paragraphStyle} key={Math.random()}>
                    {node.children.map((child, idx) => renderInlineNode(child, idx))}
                </Text>
            );

        case 'list':
            if (onlyCode) return null;
            return node.children?.map((item, idx) => (
                <View
                    key={idx}
                    style={{
                        flexDirection: 'row',
                        marginLeft: level * 10,
                        marginBottom: 3,
                    }}
                >
                    <Text style={{ width: 10 }}>{level === 0 ? '•' : '\u2013'}</Text>
                    <View style={{ flex: 1 }}>
                        {item.children?.map(child =>
                            renderNodeToPDF(child, level + 1, onlyCode)
                        )}
                    </View>
                </View>
            ));

        case 'code':
            if (node.lang === 'json') {
                try {
                    const jsonData = JSON.parse(node.value ?? '{}');
                    if (!jsonData.table) return null;
                    const data: TableData[] = jsonData.table;
                    const headers = Object.keys(data[0] ?? {});
                    return (
                        <View style={styles.table} key={Math.random()}>
                            <View
                                style={[
                                    styles.tableRow,
                                    {
                                        backgroundColor: '#2a3740',
                                        borderColor: '#2a3740',
                                        borderWidth: 1,
                                    },
                                ]}
                            >
                                {headers.map(header => (
                                    <Text
                                        style={[styles.tableCell, styles.tableCellHeader]}
                                        key={header}
                                    >
                                        {header}
                                    </Text>
                                ))}
                            </View>
                            {data.map((row, i) => (
                                <View style={styles.tableRow} key={i}>
                                    {headers.map(header => (
                                        <Text
                                            style={[
                                                styles.tableCell,
                                                { paddingVertical: 2, paddingHorizontal: 4 },
                                            ]}
                                            key={header}
                                        >
                                            {String(row[header] ?? '')}
                                        </Text>
                                    ))}
                                </View>
                            ))}
                        </View>
                    );
                } catch {
                    return null;
                }
            }
            return null;

        default:
            if (node.children) {
                return node.children.map(child =>
                    renderNodeToPDF(child, level, onlyCode)
                );
            }
            return null;
    }
};

export const renderMarkdownString = (
    markdown: string,
    onlyCode = false
): React.ReactNode => {
    const ast = unified().use(remarkParse).use(remarkGfm).parse(markdown) as MarkdownNode;
    // @ts-expect-error — unified parser Root.children type doesn't match MarkdownNode[] but is structurally compatible
    return ast.children.map((node, idx) => (
        <React.Fragment key={idx}>
            {renderNodeToPDF(node, 0, onlyCode)}
        </React.Fragment>
    ));
};

export const markdownContainsCodeBlock = (markdown: string): boolean => {
    const ast = unified().use(remarkParse).use(remarkGfm).parse(markdown) as MarkdownNode;
    const hasCodeBlock = (nodes: MarkdownNode[]): boolean =>
        nodes.some(
            node => node.type === 'code' || (node.children && hasCodeBlock(node.children))
        );
    // @ts-expect-error — unified parser Root.children type doesn't match MarkdownNode[] but is structurally compatible
    return hasCodeBlock(ast.children);
};
